import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Color, Style } from 'src/app/components/company/models/new/style';
import { Selectable } from 'src/app/shared/types/selectable.type';
import * as _ from 'lodash';

@Component({
	selector: 'bt-manage-colors-modal',
	standalone: false,
	templateUrl: './manage-colors-modal.component.html',
	styleUrl: './manage-colors-modal.component.scss'
})
export class ManageColorsModalComponent {
	readonly data = inject<{ style: Style }>(MAT_DIALOG_DATA);
	readonly dialogRef = inject(MatDialogRef<ManageColorsModalComponent>);

	colors: Selectable<Color>[] = [];

	constructor() {
		if (!this.data?.style) {
			return;
		}

		const selectedColorIds = new Set((this.data.style.colors ?? []).map((c) => c.id));
		const availableColors = this.getAvailableColors(this.data.style);

		this.colors = availableColors.map((color) => ({
			...color,
			selected: selectedColorIds.has(color.id)
		}));

		this.colors.sort((a, b) => (a.name || a.code || '').localeCompare(b.name || b.code || ''));
	}

	private getAvailableColors(style: Style): Color[] {
		const fromSchemas = _.uniqBy(
			_.flatten((style.schemas ?? []).map((schema) => schema.schemaTeamColors ?? []))
				.filter((stc) => stc.colorId != null)
				.map(
					(stc) =>
						new Color({
							id: stc.colorId,
							code: stc.colorCode,
							name: stc.color?.name || stc.colorCode
						})
				),
			'id'
		);

		if (fromSchemas.length) {
			return fromSchemas;
		}

		return _.uniqBy(style.colors ?? [], 'id').map(
			(color) => new Color({ id: color.id, code: color.code, name: color.name })
		);
	}

	toggleColor(color: Selectable<Color>): void {
		color.selected = !color.selected;
	}

	allColorsSelected(): boolean {
		return !!this.colors.length && this.colors.every((c) => c.selected);
	}

	toggleSelectAll(): void {
		const allSelected = this.allColorsSelected();
		this.colors.forEach((c) => (c.selected = !allSelected));
	}

	disableApply(): boolean {
		return !this.colors.some((c) => c.selected);
	}

	close(): void {
		this.dialogRef.close(null);
	}

	apply(): void {
		this.dialogRef.close(this.colors.filter((c) => c.selected));
	}
}
